import React, { useEffect, useId, useMemo, useState } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ChevronDownIcon } from '../react-icons/roundedTriangle.svg';
import '../styles/WeatherWidget.css';

interface WeatherCondition {
  description: string;
  icon: string;
}

interface WeatherHour {
  dt: number;
  temp: number;
  wind_speed: number;
  wind_deg: number;
  wind_gust?: number;
  wave_height?: number;
  weather: WeatherCondition[];
}

interface WeatherData {
  hourly: WeatherHour[];
}

type LocationMode = 'current' | 'fallback';

const FALLBACK_LOCATION = { lat: 41.3851, lon: 2.1734 };

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const normalizeWeather = (value: unknown): WeatherData => {
  if (typeof value !== 'object' || value === null) throw new Error('Invalid weather payload.');
  const hourly = (value as { hourly?: unknown }).hourly;
  if (!Array.isArray(hourly)) throw new Error('Hourly forecast is unavailable.');

  const normalized = hourly
    .filter((hour): hour is Record<string, unknown> => typeof hour === 'object' && hour !== null)
    .flatMap((hour) => {
      if (
        !isFiniteNumber(hour.dt) ||
        !isFiniteNumber(hour.temp) ||
        !isFiniteNumber(hour.wind_speed) ||
        !isFiniteNumber(hour.wind_deg)
      ) {
        return [];
      }

      const condition = Array.isArray(hour.weather)
        ? hour.weather.find(
            (item): item is Record<string, unknown> => typeof item === 'object' && item !== null,
          )
        : undefined;
      const description = typeof condition?.description === 'string' ? condition.description : '';
      const icon = typeof condition?.icon === 'string' ? condition.icon : '';

      return [
        {
          dt: hour.dt,
          temp: hour.temp,
          wind_speed: hour.wind_speed,
          wind_deg: hour.wind_deg,
          wind_gust: isFiniteNumber(hour.wind_gust) ? hour.wind_gust : undefined,
          wave_height: isFiniteNumber(hour.wave_height) ? hour.wave_height : undefined,
          weather: description || icon ? [{ description, icon }] : [],
        },
      ];
    })
    .slice(0, 12);

  if (normalized.length === 0) throw new Error('Hourly forecast is empty.');
  return { hourly: normalized };
};

const ThinArrowIcon: React.FC<{ degrees: number }> = ({ degrees }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    className="wind-dir"
    style={{ transform: `rotate(${degrees}deg)` }}
    aria-hidden="true"
  >
    <path
      d="M8 14 L8 2"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M8 2 L5 5.5 M8 2 L11 5.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

export const WeatherWidget: React.FC = () => {
  const { t } = useTranslation('common');
  const contentId = `weather-content-${useId()}`;
  const headingId = `weather-heading-${useId()}`;
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [locationMode, setLocationMode] = useState<LocationMode>('fallback');
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const apiBase = process.env.REACT_APP_API_BASE_URL;

    const resolveLocation = async (): Promise<{
      lat: number;
      lon: number;
      mode: LocationMode;
    }> => {
      if (!navigator.geolocation) return { ...FALLBACK_LOCATION, mode: 'fallback' };

      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) =>
            resolve({
              lat: position.coords.latitude,
              lon: position.coords.longitude,
              mode: 'current',
            }),
          () => resolve({ ...FALLBACK_LOCATION, mode: 'fallback' }),
          { enableHighAccuracy: false, timeout: 7000, maximumAge: 300_000 },
        );
      });
    };

    const fetchWeather = async () => {
      setLoading(true);
      setError(false);

      if (!apiBase) {
        setWeather(null);
        setError(true);
        setLoading(false);
        return;
      }

      try {
        const location = await resolveLocation();
        if (controller.signal.aborted) return;

        const response = await axios.get<unknown>(`${apiBase}/api/weather`, {
          params: { lat: location.lat, lon: location.lon },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        setWeather(normalizeWeather(response.data));
        setLocationMode(location.mode);
        setUpdatedAt(new Date());
      } catch {
        if (controller.signal.aborted) return;
        setWeather(null);
        setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void fetchWeather();
    return () => controller.abort();
  }, [requestVersion]);

  const firstHour = weather?.hourly[0] ?? null;
  const summary = useMemo(() => {
    if (!firstHour) return null;
    return {
      temperature: `${Math.round(firstHour.temp)}°`,
      wind: `${Math.round(firstHour.wind_speed * 1.943_84)} ${t('weather.knots', 'kn')}`,
    };
  }, [firstHour, t]);
  const locationLabel =
    locationMode === 'current'
      ? t('weather.currentLocation', 'Current location')
      : t('weather.fallbackLocation', 'Barcelona · fallback location');
  const updatedLabel = updatedAt
    ? t('weather.updatedAt', 'Updated {{time}}', {
        time: updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      })
    : '';
  const forecastTitle = t('weather.forecast', '12-Hour Forecast');

  if (loading) {
    return (
      <section
        className="weather-widget weather-widget--loading"
        aria-busy="true"
        aria-labelledby={headingId}
      >
        <div className="weather-widget-header">
          <div>
            <h2 id={headingId}>{forecastTitle}</h2>
            <p>{t('weather.loading', 'Updating local weather')}</p>
          </div>
        </div>
        <div className="weather-skeleton" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </section>
    );
  }

  if (error || !weather) {
    return (
      <section className="weather-widget weather-widget--error" aria-labelledby={headingId}>
        <div className="weather-widget-header">
          <div>
            <h2 id={headingId}>{forecastTitle}</h2>
            <p role="alert">
              {t(
                'weather.loadError',
                'Weather could not be updated. Check the connection and try again.',
              )}
            </p>
          </div>
          <button
            type="button"
            className="weather-retry"
            onClick={() => setRequestVersion((version) => version + 1)}
          >
            {t('weather.retry', 'Retry')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="weather-widget" aria-labelledby={headingId}>
      <div className="weather-widget-header">
        <div className="weather-title-group">
          <h2 id={headingId}>{forecastTitle}</h2>
          <p>
            <span>{locationLabel}</span>
            {updatedLabel && <span>{updatedLabel}</span>}
          </p>
        </div>

        <div className="weather-header-actions">
          {summary && (
            <div
              className="weather-summary"
              aria-label={t('weather.currentSummary', 'Current forecast summary')}
            >
              <span>{summary.temperature}</span>
              <span>{summary.wind}</span>
            </div>
          )}
          <button
            type="button"
            className="weather-toggle"
            aria-expanded={isExpanded}
            aria-controls={contentId}
            aria-label={
              isExpanded
                ? t('weather.collapse', 'Collapse hourly forecast')
                : t('weather.expand', 'Expand hourly forecast')
            }
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            <ChevronDownIcon aria-hidden="true" focusable="false" />
          </button>
        </div>
      </div>

      <div id={contentId} className="weather-content" hidden={!isExpanded}>
        <ol className="hourly-forecast" aria-label={t('weather.hourlyLabel', 'Hourly forecast')}>
          {weather.hourly.map((hour) => {
            const condition = hour.weather[0];
            return (
              <li key={hour.dt} className="hour-item">
                <time className="hour-time" dateTime={new Date(hour.dt * 1000).toISOString()}>
                  {new Date(hour.dt * 1000).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </time>
                {condition?.icon && (
                  <img
                    src={`https://openweathermap.org/img/wn/${condition.icon}@2x.png`}
                    alt={condition.description}
                    className="hour-icon"
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <span className="hour-temp">{Math.round(hour.temp)}°</span>
                <div className="hour-wind">
                  <span className="wind-speed">
                    {Math.round(hour.wind_speed * 1.943_84)} {t('weather.knots', 'kn')}
                  </span>
                  {hour.wind_gust !== undefined && (
                    <span className="wind-gust">
                      {t('weather.gust', 'G:')} {Math.round(hour.wind_gust * 1.943_84)}
                    </span>
                  )}
                  <ThinArrowIcon degrees={hour.wind_deg} />
                  <span className="wind-deg-text">{Math.round(hour.wind_deg)}°</span>
                </div>
                {hour.wave_height !== undefined && (
                  <div className="hour-wave">
                    <span>{t('weather.wave', 'Wave')}</span>
                    <strong>
                      {hour.wave_height.toFixed(1)} {t('weather.meters', 'm')}
                    </strong>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};
